import React from "react";
// ANT DESIGN COMPONENTS
import { InputNumber, Select, Switch } from "antd";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
// FLATBUFFER ENUMS
import { TimestampsToReturn } from "../../../../../flatbuffers/generated/opc-ua/timestamps-to-return";
// INTERFACES
import type { OpcUaSessionForm } from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Option } = Select;

export const BLANK_SESSION: OpcUaSessionForm = {
  sessionTimeoutMs: 60000,
  secureChannelLifetimeMs: 600000,
  requestTimeoutMs: 10000,
  keepAliveIntervalMs: 10000,
  sessionRetryLimit: 10,
  sessionRetryIntervalMs: 5000,
  preferredLocales: [],
  maxChunkCount: 0,
  maxMessageSize: 0,
  timestampsToReturn: TimestampsToReturn.Both,
};

const TIMESTAMPS_OPTIONS: { value: TimestampsToReturn; label: string }[] = [
  { value: TimestampsToReturn.Source, label: "Source" },
  { value: TimestampsToReturn.Server, label: "Server" },
  { value: TimestampsToReturn.Both, label: "Both" },
  { value: TimestampsToReturn.Neither, label: "Neither" },
];

/** Session and secure-channel tuning parameters. Optional — a switch toggles between server defaults (null) and an explicit config. */
export default function SessionEditor({
  session,
  onChangeSession,
}: {
  session: OpcUaSessionForm | null;
  onChangeSession: (session: OpcUaSessionForm | null) => void;
}) {
  const onToggleEnabled = (enabled: boolean) =>
    onChangeSession(enabled ? { ...BLANK_SESSION } : null);
  const onChangeField = <K extends keyof OpcUaSessionForm>(
    field: K,
    value: OpcUaSessionForm[K],
  ) => {
    onChangeSession({ ...(session ?? BLANK_SESSION), [field]: value });
  };
  const onChangeLocalesCsv = (csv: string) => {
    const locales = csv
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    onChangeField("preferredLocales", locales);
  };

  return (
    <div className="container mt-3">
      <div className="generalStyles__flex" style={{ marginBottom: 16 }}>
        <span className="generalStyles__inputLabel">
          Configure session tuning (else server defaults)
        </span>
        <Switch checked={session !== null} onChange={onToggleEnabled} style={{ marginLeft: 8 }} />
      </div>
      {session && (
        <div className="row">
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Session Timeout (ms)</span>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={0}
              value={session.sessionTimeoutMs}
              onChange={(v) => onChangeField("sessionTimeoutMs", v ?? 0)}
            />
          </div>
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Secure Channel Lifetime (ms)</span>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={0}
              value={session.secureChannelLifetimeMs}
              onChange={(v) => onChangeField("secureChannelLifetimeMs", v ?? 0)}
            />
          </div>
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Request Timeout (ms)</span>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={0}
              value={session.requestTimeoutMs}
              onChange={(v) => onChangeField("requestTimeoutMs", v ?? 0)}
            />
          </div>
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Keep-Alive Interval (ms)</span>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={0}
              value={session.keepAliveIntervalMs}
              onChange={(v) => onChangeField("keepAliveIntervalMs", v ?? 0)}
            />
          </div>
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Session Retry Limit</span>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={-1}
              value={session.sessionRetryLimit}
              onChange={(v) => onChangeField("sessionRetryLimit", v ?? 0)}
            />
          </div>
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Session Retry Interval (ms)</span>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={0}
              value={session.sessionRetryIntervalMs}
              onChange={(v) => onChangeField("sessionRetryIntervalMs", v ?? 0)}
            />
          </div>
          <div className="col-12 col-md-6 mt-2">
            <FormInputText
              inputValue={session.preferredLocales.join(",")}
              inputLabelName="Preferred Locales (comma-separated, e.g. en-US,es-ES)"
              inputOnChange={onChangeLocalesCsv}
              inputVariableName="preferredLocales"
              isRequired={false}
              disabledInput={false}
            />
          </div>
          <div className="col-12 col-md-3 mt-2">
            <span className="generalStyles__inputLabel">Max Chunk Count</span>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={0}
              value={session.maxChunkCount}
              onChange={(v) => onChangeField("maxChunkCount", v ?? 0)}
            />
          </div>
          <div className="col-12 col-md-3 mt-2">
            <span className="generalStyles__inputLabel">Max Message Size (bytes)</span>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={0}
              value={session.maxMessageSize}
              onChange={(v) => onChangeField("maxMessageSize", v ?? 0)}
            />
          </div>
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Timestamps To Return</span>
            <Select
              className="generalStyles__width100"
              size="large"
              value={session.timestampsToReturn}
              onChange={(v: TimestampsToReturn) => onChangeField("timestampsToReturn", v)}
            >
              {TIMESTAMPS_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}
